<template>
  <div :class="['view-and-compare-your-shortlisted-vehicles-component', view]">

    <div class="container-fluid">
      <div class="row">
        <div class="col-8">
          <h1>{{ product.comparison_heading }}</h1>
          <p>{{
              product.comparison_description ? product.comparison_description.slice(0, 250) : ''
            }}<span class="read-more"
              v-if="!readingMore && product.comparison_description && (product.comparison_description.length > 250)"
              @click="showModal">... <span>read more</span></span>
          </p>
          <text-modal-component v-if="readingMore" @close="showModal">
            <template v-slot:header>
              <h3>{{ product.comparison_heading }}</h3>
            </template>
            <template v-slot:body>
              <p>{{ product.comparison_description }}</p>
            </template>
          </text-modal-component>
        </div>
        <div class="col-4">
          <div class="lightswitch mt-5">
            <input type="checkbox" name="confirm" id="confirm" class="confirm" v-model="sideBySide">
            <label for="confirm">{{ product.comparison_compare_switch_text }}</label>
          </div>
        </div>
      </div>
    </div>

    <div class="container-fluid flex-fill">

      <div class="row shortlists-row">
        <car-comparison-vertical-carousel-component
          v-if="leftShortlist && session.driverProfile"
          :key="`shortlist-${leftShortlist.shortListID}-left`"
          :panel="'left'"
          :adminPortalData="adminPortalData"
          :sideBySide="sideBySide"
          :selectedQuote="selectedQuote"
          :jumpTo="jumpTo"
          :options="shortlistOptions"
          :view="view"
          :shortlist="leftShortlist"
          :session="session"
          :shortslists="shortlists"
          :annualMileage="annualMileage"
          :homeChargingStandardPercentage="homeChargingStandardPercentage"
          :publicChargingStandardPercentage="publicChargingStandardPercentage"
          :publicChargingFastPercentage="publicChargingFastPercentage"
          :publicChargingFreePercentage="publicChargingFreePercentage"
          :saveConfigurationInline="saveConfigurationInline"
          :hasInlineConfiguration="hasInlineConfiguration"
          :driverGradesById="session.driverProfile.driverGrades[this.route.params.scheme].grades"
          classes=""
          @selectedQuote="select($event)"
          @changeShortList="changeShortList('left', $event)"
          @viewPreviousQuoteGroup="jumpTo = null"
          @viewNextQuoteGroup="jumpTo = null"
        />

        <car-comparison-vertical-carousel-component
          v-if="rightShortlist && sideBySide && session.driverProfile"
          :key="`shortlist-${rightShortlist.shortListID}-right`"
          :panel="'right'"
          :adminPortalData="adminPortalData"
          :sideBySide="sideBySide"
          :showRemoveButton="true"
          :selectedQuote="selectedQuote"
          :jumpTo="jumpTo"
          :options="shortlistOptions"
          :view="view"
          :shortlist="rightShortlist"
          :session="session"
          :shortslists="shortlists"
          :annualMileage="annualMileage"
          :homeChargingStandardPercentage="homeChargingStandardPercentage"
          :publicChargingStandardPercentage="publicChargingStandardPercentage"
          :publicChargingFastPercentage="publicChargingFastPercentage"
          :publicChargingFreePercentage="publicChargingFreePercentage"
          :saveConfigurationInline="saveConfigurationInline"
          :hasInlineConfiguration="hasInlineConfiguration"
          :driverGradesById="session.driverProfile.driverGrades[this.route.params.scheme].grades"
          classes="steelBlue"
          @selectedQuote="select($event)"
          @changeShortList="changeShortList('right', $event)"
          @viewPreviousQuoteGroup="jumpTo = null"
          @viewNextQuoteGroup="jumpTo = null"
        />
      </div>
    </div>
    <footer-text v-if="footerText" :footerText="footerText" :fluid="true"/>
  </div>
</template>

<script>

import {EventBus} from '@/event-bus.js';
import FooterText from "../footer/footer-text";
import CarComparisonVerticalCarouselComponent from "./car-comparison-vertical-carousel-component";
import TextModalComponent from "../text-modal-component";
import LoadingIcon from "../loading-icon.vue";
import {useRoute} from "vue-router";

export default {
  components: {LoadingIcon, FooterText, CarComparisonVerticalCarouselComponent, TextModalComponent},
  props: [
    'adminPortalData',
    'session',
    'saveConfigurationInline',
  ],

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.get();
      vm.getChargingProfile();
    })
  },

  beforeRouteLeave(to, from, next) {

    // If we're going to the order screen - attempt to create an order.
    // todo: move to own controller
    if (to.name === 'ECO / AMAPS Personal Details' || to.name === 'Salary Sacrifice Personal Details' || to.name === 'Business Contract Hire Personal Details') {

      if (!this.selectedQuote.quoteID) {
        EventBus.$emit('showError', true);
        EventBus.$emit('errorTitle', 'Please make a selection');
        EventBus.$emit('errorMessage', 'You must select one car from one of your shortlists to continue');
        return false;
      }

      if (!this.hasInlineConfiguration && !this.shortlistItemConfigured()) {
        EventBus.$emit('showError', true);
        EventBus.$emit('errorTitle', 'Please configure your vehicle');
        EventBus.$emit('errorMessage', 'You must select a paintwork and trim from the configuration screen');
        return false;
      }

      next();

    } else {
      next();
    }
  },

  data() {
    return {
      readingMore: false,
      sideBySide: false,
      selectedQuote: {
        quote: null,
        quoteID: null,
        quoteName: null,
        type: null
      },
      shortlists: [],
      leftShortlist: null,
      rightShortlist: null,
      jumpTo: null,
      sortBy: 'monthly-rental-cost',
      orderBy: true,
      view: 'grid',

      order: {
        customerOrderRef: 'CUST123',
        quotes: [
          {
            quoteID: null,
            quantity: 1
          }
        ]
      },

      windowWidth: window.innerWidth,

      homeChargingStandardPercentage: 25, // default, changes on load
      publicChargingStandardPercentage: 25, // default, changes on load
      publicChargingFastPercentage: 25, // default, changes on load
      publicChargingFreePercentage: 25, // default, changes on load

      annualMileage: 10000,
    }
  },

  setup() {
    const route = useRoute();
    return { route }
  },

  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    })

    if (this.windowWidth <= 1024) {
      this.view = 'grid'
    }

    EventBus.$on('slideout-visible', (status) => {
      this.slideoutVisible = status;
    });

    EventBus.$on('charging-profile-updated', (values) => {
      this.homeChargingStandardPercentage = values.homeChargingStandardPercentage;
      this.publicChargingStandardPercentage = values.publicChargingStandardPercentage;
      this.publicChargingFastPercentage = values.publicChargingFastPercentage;
      this.publicChargingFreePercentage = values.publicChargingFreePercentage;
    });

    EventBus.$on('toggle-sidebyside', () => {
      this.sideBySide = !!this.sideBySide;
    });
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },

  watch: {
    windowWidth(newWidth, oldWidth) {
      if (newWidth <= 1024) {
        this.view = 'grid'
      }
    },
  },

  computed: {
    hasInlineConfiguration() {
      return this.route.params.scheme === 'bch' ? this?.adminPortalData?.content?.products?.bch[0]?.configure_show_minimal_options : false;
    },

    shortlistOptions() {
      const a = this.adminPortalData.content
        ? collect(this.shortlists).transform((item) => {
          if (item.alternateName === this.route.params.scheme) {
            return {
              shortListID: item.shortListID,
              alternateName: 'Your ' + this.adminPortalData.content.products[item.alternateName][0].name + ' Shortlist',
              quotes: item.quotes
            }
          }
        }).filter((value) => !!value).all()
        : [];

      return a;
    },

    customerReference() {
      if (this.session.profile) {
        return this.session.profile.driverID + '-' + Date.now();
      }
    },

    footerText() {
      return this.adminPortalData.content?.products?.ecoamaps
      && this.adminPortalData.content?.products?.ecoamaps[0]?.fca_requirement_and_content_fca_description_required
      && this.adminPortalData.content?.products?.ecoamaps[0]?.fca_requirement_and_content_fca_description
      && this.adminPortalData.content?.products?.ecoamaps[0]?.fca_description_required_on_compliance_configure
        ? this.adminPortalData.content.products.ecoamaps[0].fca_requirement_and_content_fca_description
        : false;
    },
    product() {
      return this.adminPortalData.content?.products?.[this.route.params.scheme]?.[0] ?? '';
    },
    scheme() {
      return this.route.params.scheme;
    }
  },

  methods: {
    shortlistItemConfigured() {
      let configured = false;

      //TODO is this really the most efficient way of doing this?
      if (this.selectedQuote && this.selectedQuote.quote && this.selectedQuote.quote.quote.details.factoryOptions) {
        let trimConfigured, paintConfigured;
        this.selectedQuote.quote.quote.details.factoryOptions.forEach((setting) => {
          if (setting.group === 'Paintwork') {
            paintConfigured = true;
          }
          if (setting.group === 'Trim') {
            trimConfigured = true;
          }
        });
        if (trimConfigured && paintConfigured) {
          configured = true;
        }
      }

      return configured
    },

    showModal() {
      this.readingMore = !this.readingMore;
    },

    getChargingProfile() {
       this.$http.get('/api/get-charging-profile')
        .then(response => {
          this.homeChargingStandardPercentage = response.data.data.home_charging_standard_percentage;
          this.publicChargingStandardPercentage = response.data.data.public_charging_standard_percentage;
          this.publicChargingFastPercentage = response.data.data.public_charging_fast_percentage;
          this.publicChargingFreePercentage = response.data.data.public_charging_free_percentage;
        })
        .catch(err => {
          EventBus.$emit('loading', false);
          let response = err.response;

          this.$notify({
            group: 'notify',
            type: 'error',
            title: "Error",
            text: response.data.message
          });

          this.error = err.message.toString();
          this.$router.push({name: response.data.redirect});
        });
    },

    async get() {
      //await this.$parent.fetchData(['driverprofile', 'drivergrades', 'vehiclesearch/derivatives']);
      EventBus.$emit('fetchData', ['driverprofile', 'drivergrades'/*, 'vehiclesearch/derivatives'*/]); // Ensure driver profile is in session

      const messages = {
        'bch': [
          'Retrieving driver profile information',
          'Retrieving shortlisted vehicles',
          'Calculating the vehicle costs',
          'Calculating the monthly BIK costs',
          'Loading comparison page',
        ],
        'salsac': [
          'Retrieving driver profile information',
          'Retrieving shortlisted vehicles',
          'Obtaining insurance premium',
          'Calculating the vehicle costs',
          'Calculating the salary sacrifice illustrations',
          'Loading comparison page',
        ],
        'ecoamaps': [
          'Retrieving driver profile information',
          'Retrieving shortlisted vehicles',
          'Retrieving business and personal mileage',
          'Calculating the vehicle costs',
          'Calculating the vehicle net monthly costs',
          'Loading comparison page',
        ],
        'generic': [
          'Retrieving shortlisted vehicles',
          'Loading comparison page'
        ]
      };

      EventBus.$emit('loading', true);
      let interval = 1500;
      let loadingMessages = messages[this.route.params.scheme] ? messages[this.route.params.scheme] : messages['generic'];

      loadingMessages.forEach((message, index) => {
        setTimeout(() => {
          EventBus.$emit('loadingTitle', message);
        }, index * interval);
      });

      this.$http.get('/api/car-comparison')
        .then(response => {
          EventBus.$emit('loading', false);
          this.shortlists = response.data.data.data.filter(item => {
            return [this.route.params.scheme].includes(item.name);
          });

          this.setComparisonShortlists();
        })
        .catch(err => {
          let response = err.response;

          this.$notify({
            group: 'notify',
            type: 'error',
            title: "Error",
            text: response.data.message
          });

          this.error = err.message.toString();
          this.$router.push({name: response.data.redirect});
        })
        .finally(() => {
          EventBus.$emit('loading', false);
      });
    },

    setComparisonShortlists() {
      const scheme = this.route.params.scheme;
      this.leftShortlist = collect(this.shortlists).where('name', scheme).first();
      this.rightShortlist = collect(this.shortlists).where('name', scheme).first();
    },

    changeShortList(position, event) {

      this.jumpTo = null;
      let shortlist = collect(this.shortlists).where('shortListID', event).first();

      switch (position) {
        case 'left':
          this.leftShortlist = shortlist;
          break;
        case 'right':
          this.rightShortlist = shortlist;
          break;
      }
    },

    remove(shortlist, shortlistId, quoteId) {
      if (this[shortlist + 'Shortlist'].length > 1) {

        EventBus.$emit('loadingTitle', 'Removing Vehicle from Shortlist');
        EventBus.$emit('loading', true);

        this.$http.post('/api/quotation', {
          method: 'DELETE',
          endpoint: `shortlists/${shortlistId}/quotes/${quoteId}`,
        }).then(response => {
          EventBus.$emit('loading', false);
        });
      }
    },

    select(event) {
      this.selectedQuote = event;
      this.selectedQuote.scheme = this.route.params.scheme;
      this.$emit('selectQuote', this.selectedQuote)
    },

    onResize() {
      this.windowWidth = window.innerWidth
    },
  }
}
</script>
